import React from 'react';
import { Listbox } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon, XMarkIcon } from '@heroicons/react/20/solid';
import TextStack from './TextStack';
import { classNames } from '../helpers/functions';

/**
 * SelectedAssetsList - Displays selected assets with optional contract selection
 *
 * @param {Array} assets - Array of selected asset objects with full data
 * @param {Function} onRemove - Callback when removing an asset (receives assetId)
 * @param {Function} onContractChange - Callback when changing contract (receives assetId, contractId)
 * @param {Function} formatSecondary - Optional function to format secondary text (receives asset)
 * @param {String} className - Additional CSS classes
 */
const SelectedAssetsList = ({
	assets = [],
	onRemove,
	onContractChange,
	formatSecondary,
	className = '',
}) => {
	if (assets.length === 0) return null;

	const getAssetName = (asset) => {
		return (
			`${asset?.manufacturerName || asset?.manufacturer?.name || ''} ${
				asset?.modelName || asset?.model?.name || ''
			}`.trim() || 'Unknown Asset'
		);
	};

	const getSecondaryText = (asset) => {
		if (formatSecondary) {
			return formatSecondary(asset);
		}
		return asset?.serialNumber
			? `S/N: ${asset.serialNumber}`
			: asset?.name || 'No serial number';
	};

	return (
		<div className={className}>
			<p className="text-xs font-medium text-gray-500 uppercase mb-2 pb-3">
				Selected ({assets.length})
			</p>
			<div className="space-y-2">
				{assets.map((asset) => (
					<div
						key={asset.id || asset.assetId}
						className="flex items-center justify-between py-1.5 rounded-lg border-gray-200"
					>
						<div className="flex items-center gap-3 flex-1 min-w-0">
							<TextStack
								primary={getAssetName(asset)}
								secondary={getSecondaryText(asset)}
							/>
						</div>
						<div className="flex items-center gap-2 ml-3 flex-shrink-0">
							{/* Contract Selection Dropdown */}
							{onContractChange && (
								<Listbox
									value={asset.contractId}
									onChange={(contractId) =>
										onContractChange(asset.assetId || asset.id, contractId)
									}
									disabled={!asset.contracts?.length}
								>
									<div className="relative">
										<Listbox.Button
											type="button"
											className={classNames(
												'relative w-40 cursor-pointer rounded-md py-1.5 pl-3 pr-8 text-left text-xs border',
												!asset.contracts?.length
													? 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200'
													: 'bg-white border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500',
											)}
										>
											<span className="block truncate">
												{asset.contractId
													? asset.contracts?.find(
															(c) => c.id === asset.contractId,
														)?.name || 'Contract'
													: asset.contracts?.length
														? 'No contract'
														: 'No contracts'}
											</span>
											<span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
												<ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
											</span>
										</Listbox.Button>
										<Listbox.Options className="absolute right-0 z-10 mt-1 max-h-60 w-48 overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none text-xs">
											<Listbox.Option
												value={null}
												className={({ active }) =>
													classNames(
														active ? 'bg-gray-100' : '',
														'relative cursor-pointer select-none py-2 pl-3 pr-9 text-gray-600',
													)
												}
											>
												{({ selected }) => (
													<>
														<span>No contract</span>
														{selected && (
															<span className="absolute inset-y-0 right-0 flex items-center pr-3 text-indigo-600">
																<CheckIcon className="h-4 w-4" />
															</span>
														)}
													</>
												)}
											</Listbox.Option>
											{asset.contracts?.map((contract) => (
												<Listbox.Option
													key={contract.id}
													value={contract.id}
													className={({ active }) =>
														classNames(
															active ? 'bg-indigo-50' : '',
															'relative cursor-pointer select-none py-2 pl-3 pr-9',
														)
													}
												>
													{({ selected }) => (
														<>
															<div>
																<span className="block truncate font-medium">
																	{contract.name}
																</span>
																{contract.active && (
																	<span className="text-green-600 text-[10px]">
																		Active
																	</span>
																)}
															</div>
															{selected && (
																<span className="absolute inset-y-0 right-0 flex items-center pr-3 text-indigo-600">
																	<CheckIcon className="h-4 w-4" />
																</span>
															)}
														</>
													)}
												</Listbox.Option>
											))}
										</Listbox.Options>
									</div>
								</Listbox>
							)}

							{/* Remove Button */}
							{onRemove && (
								<button
									type="button"
									onClick={() => onRemove(asset.assetId || asset.id)}
									className="p-1 text-gray-400 hover:text-red-500"
								>
									<XMarkIcon className="h-4 w-4" />
								</button>
							)}
						</div>
					</div>
				))}
			</div>
		</div>
	);
};

export default SelectedAssetsList;